
import { toastController } from '@ionic/vue'
import utils from '../utils/toast.utils'
import { closeOutline } from 'ionicons/icons'

// ToastOptions type - using any to match Ionic's ToastOptions
type ToastOptions = any

/**
 * @see [Documentation]{@link (https://ionicframework.com/docs/api/toast)} for more information about the toast.
 */
export class Toast {
	private readonly MINIMUM_DESKTOP_WIDTH = 768
	private static readonly CSS_MEDIA_BREAKPOINT = 1354 // CSS uses @media screen and (min-width: 1354px)
	private static readonly TOAST_OFFSET = 60 // Offset v px medzi toast hláškami
	private static activeToasts: Map<string, any[]> = new Map()

	private i18n
	private duration
	private buttons = [
		{
			icon: closeOutline,
			role: 'cancel',
			handler: () => {
				toastController.dismiss()
			}
		}
	]

	constructor(i18n, duration = 8000) {
		this.i18n = i18n.global
		this.duration = duration
	}

	/**
	 * Generates a unique key for toast position tracking
	 */
	private getPositionKey(verticalPosition: string, horizontalPosition: string): string {
		return `${verticalPosition}-${horizontalPosition}`
	}

	/**
	 * Registers a toast in the active toasts map and sets its CSS custom property for positioning
	 */
	private registerToast(toast: any, verticalPosition: string, horizontalPosition: string): void {
		const key = this.getPositionKey(verticalPosition, horizontalPosition)
		const toasts = Toast.activeToasts.get(key) || []
		toasts.push(toast)
		Toast.activeToasts.set(key, toasts)
	}
	
	/**
	 * Updates toast position in DOM
	 */
	private updateToastPosition(toastElement: HTMLElement, index: number, verticalPosition: string, horizontalPosition?: string): void {
		if (!toastElement) return
		
		const offset = index * Toast.TOAST_OFFSET
		toastElement.classList.add('w-toast-stack')
		
		// Remove any existing bottom style for top/middle positions
		if (verticalPosition !== 'bottom') {
			toastElement.style.bottom = ''
		}
		
		// Check if this is a center toast - check both class and parameter
		const hasWCenterClass = toastElement.classList.contains('w-center')
		const isCenterByParam = horizontalPosition === 'center'
		// Check if screen is smaller than CSS media breakpoint - on smaller screens, CSS position rules don't apply and toasts become center
		const isSmallScreen = window.innerWidth < Toast.CSS_MEDIA_BREAKPOINT
		// On small screens (< 1354px), CSS position rules don't apply, so all toasts are effectively center
		const isCenter = hasWCenterClass || isCenterByParam || isSmallScreen
		
		if (verticalPosition === 'top') {
			// For top position, move down from 50%
			toastElement.style.top = `calc(50% + ${offset}px)`
		} else if (verticalPosition === 'bottom') {
			// For bottom position, move up from 50% (use top with negative offset)
			toastElement.style.top = `calc(50% - ${offset}px)`
		} else if (verticalPosition === 'middle') {
			// For middle position, move down from 4% + 4px
			toastElement.style.top = `calc(4% + 4px + ${offset}px)`
		}
		
		// For center toasts, use translate(0%, -50%) since left: 50% already centers horizontally
		// For other toasts, use translate(-50%, -50%) for proper centering
		if (isCenter) {
			toastElement.style.transform = 'translate(0%, -50%)'
		} else {
			toastElement.style.transform = 'translate(-50%, -50%)'
		}
	}

	/**
	 * Unregisters a toast from the active toasts map and updates positions of remaining toasts
	 */
	private unregisterToast(toast: any, verticalPosition: string, horizontalPosition: string): void {
		const key = this.getPositionKey(verticalPosition, horizontalPosition)
		const toasts = Toast.activeToasts.get(key) || []
		const index = toasts.indexOf(toast)
		
		if (index !== -1) {
			toasts.splice(index, 1)
			Toast.activeToasts.set(key, toasts)
			
			// Update positions of remaining toasts
			const positionClass = `w-${verticalPosition}`
			setTimeout(() => {
				const toastElements = document.querySelectorAll(`ion-toast.${positionClass}.w-toast-stack`) as NodeListOf<HTMLElement>
				toasts.forEach((remainingToast, newIndex) => {
					if (toastElements[newIndex]) {
						this.updateToastPosition(toastElements[newIndex], newIndex, verticalPosition, horizontalPosition)
					}
				})
			}, 50)
		}
	}

	/**
	 * Creates a toast with stack support
	 */
	private async createToast(options: ToastOptions, verticalPosition: string, horizontalPosition: string): Promise<void> {
		const toast = await toastController.create(options)
		
		// Register toast before presenting
		this.registerToast(toast, verticalPosition, horizontalPosition)
		
		// Handle toast dismissal
		toast.onDidDismiss().then(() => {
			this.unregisterToast(toast, verticalPosition, horizontalPosition)
		})

		await toast.present()
		
		// Update CSS after toast is in DOM - wait a bit for Ionic to render
		const positionClass = `w-${verticalPosition}`
		const key = this.getPositionKey(verticalPosition, horizontalPosition)
		const toasts = Toast.activeToasts.get(key) || []
		const index = toasts.length - 1
		
		// Try multiple times to find the toast element (Ionic might need time to render)
		const updatePosition = (attempt = 0) => {
			// Find all toasts with the position class and update the one at our index
			const toastElements = Array.from(document.querySelectorAll(`ion-toast.${positionClass}`)) as HTMLElement[]
			
			// Update all toasts in the stack to ensure correct positioning
			toasts.forEach((_, idx) => {
				if (toastElements[idx]) {
					this.updateToastPosition(toastElements[idx], idx, verticalPosition, horizontalPosition)
				}
			})
			
			// If we didn't find the element yet, try again
			if (toastElements.length === 0 && attempt < 10) {
				setTimeout(() => updatePosition(attempt + 1), 100)
			}
		}
		
		setTimeout(() => updatePosition(), 150)
	}

	/**
	 * Creates success toast.
	 * @param message - (Optional) Message to be displayed in the toast.
	 * @param verticalPosition - (Optional) Vertical position of the toast. Default: 'top'.
	 * @param horizontalPosition - (Optional) Horizontal position of the toast. Default: 'right'.
	 */
	public async success(message = 'Success', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await this.createToast({
			message: utils.parseAndTranslate(message, this.i18n),
			color: 'success',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= this.MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: this.duration,
			buttons: this.buttons
		}, verticalPosition, horizontalPosition)
	}

	/**
	 * Creates info toast.
	 * @param message - (Optional) Message to be displayed in the toast.
	 * @param verticalPosition - (Optional) Vertical position of the toast. Default: 'top'.
	 * @param horizontalPosition - (Optional) Horizontal position of the toast. Default: 'right'.
	 */
	public async info(message = 'Success', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await this.createToast({
			message: utils.parseAndTranslate(message, this.i18n),
			color: 'primary',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= this.MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: this.duration,
			buttons: this.buttons
		}, verticalPosition, horizontalPosition)
	}

	/**
	 * Creates warning toast.
	 * @param message - (Optional) Message to be displayed in the toast.
	 * @param verticalPosition - (Optional) Vertical position of the toast. Default: 'top'.
	 * @param horizontalPosition - (Optional) Horizontal position of the toast. Default: 'right'.
	 */
	public async warning(message = 'Warning', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await this.createToast({
			message: utils.parseAndTranslate(message, this.i18n),
			color: 'warning',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= this.MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: this.duration,
			buttons: this.buttons
		}, verticalPosition, horizontalPosition)
	}

	/**
	 * Creates error toast.
	 * @param message - (Optional) Message to be displayed in the toast.
	 * @param verticalPosition - (Optional) Vertical position of the toast. Default: 'top'.
	 * @param horizontalPosition - (Optional) Horizontal position of the toast. Default: 'right'.
	 */
	public async error(message = 'Error', verticalPosition: 'top' | 'middle' | 'bottom' = 'top', horizontalPosition: 'left' | 'center' | 'right' = 'right') {
		await this.createToast({
			message: utils.parseAndTranslate(message, this.i18n),
			color: 'danger',
			position: verticalPosition,
			cssClass: `w-toast w-${verticalPosition} ${horizontalPosition && window.innerWidth >= this.MINIMUM_DESKTOP_WIDTH ? `w-${horizontalPosition}` : ''}`,
			duration: this.duration,
			buttons: this.buttons
		}, verticalPosition, horizontalPosition)
	}

	/**
	 * Creates custom toast.
	 * @param options - (Optional) Options to be displayed in the toast. See [Documentation]{@link (https://ionicframework.com/docs/api/toast#toastoptions)} for more information about the toast options.
	 */
	public async custom(options: ToastOptions) {
		const verticalPosition = options.position || 'top'
		const horizontalPosition = options.cssClass?.includes('w-left') ? 'left' : options.cssClass?.includes('w-center') ? 'center' : 'right'
		await this.createToast(options, verticalPosition, horizontalPosition)
	}
}
